<template>
  <header
    v-disableable="{blur: true, dim: true, disabled: $store.getters['session/onTour']}"
    class="topbar-component-container"
    :class="{ mastery: isMasteryApp }"
  >
    <router-link
      :to="{name: 'home'}"
      class="app-logo"
    >
      <img
        :class="{ ase: certApp }"
        :src="$store.getters['app/appLogo']"
        aria-labelledby="app-name"
      >
    </router-link>
    <div class="title">
      <router-link
        v-if="!certApp"
        id="app-name"
        :to="{name: 'home'}"
      >
        {{ $store.getters['app/appName'] }}
      </router-link>
    </div>
    <searchbar-default
      v-if="showSearchbar && !isPhone"
      :context="'topbar'"
    />
    <div class="spacer" />
    <beta-toggle />
    <template v-if="$store.getters['session/authenticated'] && $store.getters['session/currentUser']">
      <div class="account-dropdown-container">
        <button
          v-if="showUpgrade"
          class="upgrade-button"
          @click="upgradeClick"
        >
          <icon
            name="lock"
            variant="516173"
            class="upgrade-icon"
          />
          {{ upgradeName }}
        </button>
        <button
          v-else-if="asePurchaseEligible"
          class="cert-upgrade-button"
          @click="upgradeClick"
        >
          {{ aseSubscriptionActionText }}
        </button>
        <div class="hello-user">
          <span>Hello{{ name }}!</span>
          <span v-if="user && user.previewer">
            <icon
              name="previewer"
              class="previewer-icon"
            />
          </span>
        </div>
        <button class="button-reset" data-test="profile-modal-button" @click="openProfile">
          Profile
        </button>
        <button
          class="sign-out"
          @click="signOut()"
        >
          Sign Out
        </button>
      </div>
    </template>
    <template v-else>
      <div class="account-dropdown-container">
        <router-link :to="{ name: 'signIn' }">
          Log In
        </router-link>
        <router-link :to="{ name: 'signUp' }">
          Create Account
        </router-link>
      </div>
    </template>
    <router-link
      class="support-link"
      aria-label="Support"
      :to="{name: 'support'}"
    >
      <icon
        class="support-icon"
        name="help"
        variant="516173"
      />
    </router-link>
    <div
      class="mobile-menu-button"
      @click="$store.commit('session/toggleMobileMenu', true)"
    >
      <icon
        v-if="isMasteryApp"
        name="pancake"
      />
      <icon
        v-else
        name="hamburger"
        variant="516173"
      />
    </div>
  </header>
</template>

<script>
import BetaToggle from '@components/singletons/beta-toggle';
import SearchbarDefault from '@components/singletons/SearchbarDefault';
import Icon from '@components/singletons/icon';

import Authentication from '@/mixins/authentication';
import Certification from '@/mixins/certifications';
import DeviceTypeMixin from '@/mixins/deviceType';
import TrialMixin from '@/mixins/trial';

export default {
  components: { BetaToggle, Icon, SearchbarDefault },
  mixins: [Authentication, Certification, DeviceTypeMixin, TrialMixin],
  computed: {
    asePurchaseEligible() {
      return this.certApp && this.userCanPurchase;
    },
    isMasteryApp() {
      return this.$store.getters['app/isMasteryApp'];
    },
    name() {
      if (this.user && this.user.firstName && this.user.firstName.length > 0) {
      return ` ${this.user.firstName}`;
      } else {
        return '';
      }
    },
    showSearchbar() {
      const masteryApp = this.$store.getters['app/isMasteryApp'];
      const searchbarDisabled = this.$route.meta.disableSearchbar;
      const disappearingSearchbar = this.$route.meta.disappearingSearchbar;
      if (masteryApp) {
        if (searchbarDisabled) return false;
        if (disappearingSearchbar) {
          return !this.$store.getters['search/visible'];
        }
        return true;
      } else {
        return false;
      }
    },
    showUpgrade() {
      const userHasPaid = this.$store.getters['session/hasPaid'];
      const pvar = this.$store.getters['productVars/get']['disable_web_top_bar_upgrade'];
      const disabledByPVar = pvar && (pvar.parsedValue === true);
      return !userHasPaid && !this.certApp && !disabledByPVar;
    },
    upgradeName() {
      const appPricing = this.$store.getters['app/pricingType'];
      if (this.trialEnabled) {
        return 'Start Trial';
      } else if (appPricing === 'Subscription') {
        return 'Join';
      } else {
        return 'Unlock';
      }
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    openProfile() {
      return this.$store.dispatch('ui/setProfileModalShown', 'profileShowModal');
    },
    signOut() {
      this.$store.dispatch('session/signOut');
    },
    upgradeClick() {
      this.$analytics.events.clickedUpsell(this.$store, 'Header', this.$store.getters['app/pricingType']);
      if (this.certApp) {
        this.$store.dispatch('ui/toggleUpsellPopout', { show: true });
      } else {
        this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: { type: 'Header Button' }});
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  $topbar-background: #ffffff !default;

  .topbar-component-container {
    align-items: center;
    background-color: $topbar-background;
    border-bottom: 2px solid;
    @include border-color($accent-1);
    box-sizing: border-box;
    display: flex;
    gap: rem-calc(8);
    height: rem-calc(48);
    left: 0;
    padding: rem-calc(8) rem-calc(24) rem-calc(8) rem-calc(16);
    position: absolute;
    right: 0;

    @include breakpoint(small) {
      height: rem-calc(72);
      padding: rem-calc(16) rem-calc(18) rem-calc(16) rem-calc(18);
    }

    .app-logo {
      line-height: 1px;

      img {
        height: rem-calc(32);
        margin-right: rem-calc(16);
        width: auto;;

        @include breakpoint(small) {
          height: rem-calc(36);
        }

        &.ase {
          height: rem-calc(28);

          @include breakpoint(small) {
            height: rem-calc(32);
          }
        }
      }
    }

    .title {
      color: $grey-2;
      font-size: rem-calc(16);

      a:link, a:visited {
        color: $grey-2;
      }
    }

    .spacer {
      flex: 1;
    }

    .mobile-menu-button {
      display: none;
      width: rem-calc(20);

      @include breakpoint(small) {
        display: block;
      }
    }

    .cert-upgrade-button {
      background: #ff671f;
      border-radius: 4px;
      color: white;
      display: inline-block;
      font-weight: 600;
      margin: 0 rem-calc(20);
      padding: 0.5rem 1.75rem;
    }

    .account-dropdown-container {
      align-items: center;
      color: $grey-2;
      display: flex;
      .upgrade-button {
        box-sizing: content-box;
        @include hlt-btn-1;
        height: rem-calc(30);
        margin: 0 rem-calc(20);
        font-size: rem-calc(14);
        .upgrade-icon {
          flex-shrink: 0;
          width: 16px;
          margin-right: 7px;
        }
      }
      font: {
        size: rem-calc(12);
        weight: $semi-bold;
      };
      height: 100%;
      line-height: rem-calc(21);
      position: relative;

      @include breakpoint(small) {
        display: none;
      }

      a,
      button {
        color: transparentize($grey-2, .36);
        cursor: pointer;
        font-weight: $normal;
        margin-left: rem-calc(12);

        &:visited {
          color: transparentize($grey-2, .36);
        }

        &:hover {
          color: $grey-2;
          text-decoration: underline;
        }
      }

      .sign-out {
        padding: 0;
        color: transparentize($grey-2, .36);
        margin-left: rem-calc(12);
        background: transparent;
      }
    }

    .support-link {
      margin-left: rem-calc(12);

      @include breakpoint(small) {
        display: none;
      }

      .support-icon {
        height: rem-calc(16);
        width: rem-calc(16);
      }
    }

    .beta-toggle-component-container  {
      @include breakpoint(small) {
        display: none;
      }
    }

    &.mastery {
      z-index: 900;
      position: fixed;
      top: 0;

      .title > a {
        color: black;
      }

      .account-dropdown-container {
        color: black;

        a,
        .sign-out,
        button {
          color: black !important;
        }
      }
      .mobile-menu-button {
        width: rem-calc(28);
      }
    }
  }

  .hello-user {
    display: flex;
    align-items: center;
    gap: rem-calc(4);
  }
  .previewer-icon {
    height: rem-calc(24);
  }
</style>
